import React, { memo, useCallback, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FastField } from 'formik';
import { TextField } from 'components/formik/text-field';

import { FancyRadioGroup } from 'components/formik/fancy-radio/fancy-radio-group';
import { FancyRadio } from 'components/formik/fancy-radio/fancy-radio';
import { useUserProfileQuery } from 'app/store';
import { FormCard } from './form-card';
import { IdentityFormTemplate } from '../identity.form';
import { LegalFormTemplate } from '../legal.form';
import { ContactFormTemplate } from '../contact.form';
import { AddressFormTemplate } from '../address.form';
import { SignatureFormTemplate } from '../signature.form';
import ReferralCodeField from './referral-code-field';

const ProfileFormTemplate = memo(({ props }: any): JSX.Element => {
    const {
        values: { type, signatures },
        resetForm,
        setUserType,
        onboarding = false,
        registration = false,
    } = props;

    const { data: profile } = useUserProfileQuery();

    const profileStatus = profile?.status;
    const hasAssignedReferral = registration && Boolean(profile?.referral_locked);
    const resetFormMemoized = useCallback(() => resetForm({}), [resetForm]);

    useEffect(() => {
        setUserType && setUserType(type);
    }, [type]);

    const sectionProps = {
        onboarding,
    };

    return (
        <>
            {profileStatus !== 'accept' && (
                onboarding ? (
                    <FormCard
                        {...sectionProps}
                        icon={<i className="fa-light fa-user-tag" />}
                        name="نوع حساب"
                        description="نوع فعالیت خود را مشخص کنید تا اطلاعات متناسب با حساب شما دریافت شود.">
                        <Box
                            sx={(theme) => ({
                                '& .fancy-radio': {
                                    minHeight: 70,
                                    borderRadius: '10px',
                                    backgroundColor:
                                        theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.background.paper, 0.72)
                                            : theme.palette.common.white,
                                },
                                '& .fancy-radio.active': {
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        theme.palette.mode === 'dark' ? 0.13 : 0.055,
                                    ),
                                    boxShadow: `inset 0 0 0 1px ${alpha(
                                        theme.palette.primary.main,
                                        0.08,
                                    )}`,
                                },
                            })}>
                            <FastField
                                component={FancyRadioGroup}
                                onClick={resetFormMemoized}
                                name="type"
                                defaultValue="person">
                                <FancyRadio
                                    value="person"
                                    label="حقیقی"
                                    icon={<i className="fa-light fa-clipboard-user" />}
                                />
                                <FancyRadio
                                    value="company"
                                    label="حقوقی"
                                    icon={<i className="fa-light fa-building-user" />}
                                />
                            </FastField>
                        </Box>
                    </FormCard>
                ) : (
                    <FastField
                        component={FancyRadioGroup}
                        onClick={resetFormMemoized}
                        name="type"
                        defaultValue="person">
                        <FancyRadio
                            value="person"
                            label="حقیقی"
                            icon={<i className="fa-light fa-clipboard-user" />}
                        />
                        <FancyRadio
                            value="company"
                            label="حقوقی"
                            icon={<i className="fa-light fa-building-user" />}
                        />
                    </FastField>
                )
            )}

            {type === 'person' && (
                <FormCard
                    {...sectionProps}
                    icon={<i className="fa-light fa-clipboard-user" />}
                    name="اطلاعات هویتی"
                    description={
                        onboarding
                            ? 'اطلاعات را دقیقاً مطابق مدارک هویتی خود وارد کنید.'
                            : undefined
                    }>
                    <IdentityFormTemplate
                        disableAutoFill={registration}
                        disableBirthDate={!registration}
                        initialBirthPickerView={
                            registration ? 'year' : undefined
                        }
                    />
                </FormCard>
            )}

            {type === 'company' && (
                <FormCard
                    {...sectionProps}
                    icon={<i className="fa-light fa-building-user" />}
                    name="اطلاعات حقوقی"
                    description={
                        onboarding
                            ? 'مشخصات ثبتی و حقوقی مجموعه را کامل و دقیق وارد کنید.'
                            : undefined
                    }>
                    <LegalFormTemplate
                        disableAutoFill={registration}
                        initialBirthPickerView={
                            registration ? 'year' : undefined
                        }
                    />
                </FormCard>
            )}

            <FormCard
                {...sectionProps}
                icon={<i className="fa-light fa-phone-office" />}
                name="اطلاعات تماس"
                description={
                    onboarding
                        ? 'ایمیل فعال خود را وارد کنید؛ شماره موبایل تأییدشده قابل ویرایش نیست.'
                        : undefined
                }>
                <ContactFormTemplate disableAutoFill={registration} />
            </FormCard>

            {profileStatus !== 'accept' && (
                <FormCard
                    {...sectionProps}
                    icon={<i className="fa-light fa-map-location-dot" />}
                    name="اطلاعات آدرس"
                    description={
                        onboarding
                            ? 'نشانی و اطلاعات تماس ثابت را برای تکمیل احراز هویت وارد کنید.'
                            : undefined
                    }>
                    <AddressFormTemplate
                        showTaxCode={type === 'company' && !registration}
                        hideTaxCode={registration && type === 'company'}
                        disableAutoFill={registration}
                    />
                </FormCard>
            )}

            {type === 'company' && signatures?.length > 0 && (
                <FormCard
                    {...sectionProps}
                    icon={<i className="fa-light fa-signature" />}
                    name="امضاداران"
                    description={
                        onboarding
                            ? 'اطلاعات اشخاص دارای حق امضا را بررسی و تکمیل کنید.'
                            : undefined
                    }>
                    <SignatureFormTemplate
                        signatures={signatures}
                        disableAutoFill={registration}
                    />
                </FormCard>
            )}

            {profileStatus !== 'accept' && !hasAssignedReferral && (
                <FormCard
                    {...sectionProps}
                    icon={<i className="fa-light fa-users-medical" />}
                    name="کد معرف"
                    description={
                        onboarding
                            ? 'اگر از طرف شخصی معرفی شده‌اید، کد معرف را وارد کنید. این بخش اختیاری است.'
                            : undefined
                    }>
                    {registration ? (
                        <ReferralCodeField />
                    ) : (
                        <FastField
                            fullWidth
                            name="refered"
                            label="کد معرف را وارد نمایید (اختیاری)"
                            icon={<i className="fa-light fa-users-medical" />}
                            component={TextField}
                            ltr="true"
                            maxLength={20}
                        />
                    )}
                </FormCard>
            )}

            {onboarding && (
                <Box
                    sx={(theme) => ({
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mt: 2,
                        px: 1,
                        color: 'text.secondary',
                        i: {
                            color:
                                theme.palette.mode === 'dark'
                                    ? 'primary.light'
                                    : 'primary.main',
                            fontSize: 13,
                            mt: '4px',
                        },
                    })}>
                    <i className="fa-light fa-circle-info" />
                    <Typography sx={{ fontSize: 11.5, lineHeight: 1.9 }}>
                        لطفاً اطلاعات را با دقت وارد کنید. اطلاعات نادرست می‌تواند روند بررسی حساب را طولانی‌تر کند.
                    </Typography>
                </Box>
            )}
        </>
    );
});

export default ProfileFormTemplate;
